import { useEffect, useRef, useState } from "react";
import { X, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

const AVAIL_LABELS = {
  green: { text: "LOOKING FOR A CLUB", color: "#22c55e" },
  yellow: { text: "OPEN TO OFFERS", color: "#facc15" },
  red: { text: "NOT LOOKING", color: "#ef4444" },
};

const W = 1080;
const H = 1350;

async function drawCard(canvas, profile, career) {
  await Promise.all([
    document.fonts.load("100px Anton"),
    document.fonts.load("600 40px Manrope"),
  ]);
  const ctx = canvas.getContext("2d");

  // Background
  ctx.fillStyle = "#050505";
  ctx.fillRect(0, 0, W, H);
  // Frame
  ctx.strokeStyle = "#262626";
  ctx.lineWidth = 2;
  ctx.strokeRect(40, 40, W - 80, H - 80);
  // Diagonal accent stripes top-right
  ctx.save();
  ctx.beginPath();
  ctx.rect(40, 40, W - 80, H - 80);
  ctx.clip();
  ctx.strokeStyle = "#1a1a1a";
  ctx.lineWidth = 22;
  for (let i = 0; i < 8; i++) {
    ctx.beginPath();
    ctx.moveTo(W - 420 + i * 60, 20);
    ctx.lineTo(W + 40 + i * 60, 480);
    ctx.stroke();
  }
  ctx.restore();

  // Logo
  ctx.fillStyle = "#ffffff";
  ctx.font = "68px Anton";
  ctx.textBaseline = "top";
  ctx.fillText("EGNCY", 90, 92);
  ctx.fillStyle = "#737373";
  ctx.font = "600 26px Manrope";
  ctx.fillText("WHERE FOOTBALLERS GET SEEN", 92, 176);

  // Avatar (circular)
  let textTop = 300;
  if (profile.avatar_url) {
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = "anonymous";
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = profile.avatar_url;
      });
      const cx = W - 230;
      const cy = 250;
      const r = 130;
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.clip();
      const scale = Math.max((r * 2) / img.width, (r * 2) / img.height);
      ctx.drawImage(img, cx - (img.width * scale) / 2, cy - (img.height * scale) / 2, img.width * scale, img.height * scale);
      ctx.restore();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = "#404040";
      ctx.lineWidth = 4;
      ctx.stroke();
    } catch {
      // avatar failed to load — skip it
    }
  }

  // Name (wrap on two lines if long)
  const name = (profile.name || "").toUpperCase();
  ctx.fillStyle = "#ffffff";
  let nameSize = 120;
  ctx.font = `${nameSize}px Anton`;
  const maxNameWidth = W - 180;
  const words = name.split(" ");
  let lines = [name];
  if (ctx.measureText(name).width > maxNameWidth && words.length > 1) {
    const mid = Math.ceil(words.length / 2);
    lines = [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
  }
  while (lines.some((l) => ctx.measureText(l).width > maxNameWidth) && nameSize > 60) {
    nameSize -= 8;
    ctx.font = `${nameSize}px Anton`;
  }
  textTop = 430;
  lines.forEach((l, i) => {
    ctx.fillText(l, 90, textTop + i * (nameSize + 14));
  });
  let y = textTop + lines.length * (nameSize + 14) + 26;

  // Meta line
  const meta = [profile.position, profile.team, profile.region].filter(Boolean).join("  ·  ");
  if (meta) {
    ctx.fillStyle = "#a3a3a3";
    ctx.font = "600 34px Manrope";
    ctx.fillText(meta.toUpperCase(), 92, y);
    y += 62;
  }

  // Attribute chips
  const chips = [
    profile.age ? `AGE ${profile.age}` : null,
    profile.height_cm ? `${profile.height_cm} CM` : null,
    profile.preferred_foot ? `${profile.preferred_foot.toUpperCase()} FOOT` : null,
    profile.level_of_play ? profile.level_of_play.toUpperCase() : null,
  ].filter(Boolean);
  if (chips.length) {
    ctx.font = "600 26px Manrope";
    let cx2 = 92;
    for (const chip of chips) {
      const w = ctx.measureText(chip).width + 44;
      ctx.strokeStyle = "#404040";
      ctx.lineWidth = 2;
      ctx.strokeRect(cx2, y, w, 54);
      ctx.fillStyle = "#e5e5e5";
      ctx.fillText(chip, cx2 + 22, y + 15);
      cx2 += w + 16;
    }
    y += 96;
  }

  // Stats grid
  const minutes = career.minutes || 0;
  const ga90 = minutes > 0 ? (((career.goals || 0) + (career.assists || 0)) / (minutes / 90)).toFixed(2) : null;
  const stats = [
    { label: "MATCHES", value: String(career.matches ?? 0) },
    { label: "GOALS", value: String(career.goals ?? 0) },
    { label: "ASSISTS", value: String(career.assists ?? 0) },
    ga90 !== null ? { label: "G+A / 90", value: ga90 } : { label: "MINUTES", value: String(minutes) },
  ];
  const gridY = Math.max(y, 800);
  const cellW = (W - 180 - 3 * 20) / 4;
  stats.forEach((s, i) => {
    const x = 90 + i * (cellW + 20);
    ctx.fillStyle = "#0f0f0f";
    ctx.fillRect(x, gridY, cellW, 210);
    ctx.strokeStyle = "#262626";
    ctx.lineWidth = 2;
    ctx.strokeRect(x, gridY, cellW, 210);
    ctx.fillStyle = "#ffffff";
    ctx.font = "76px Anton";
    const vw = ctx.measureText(s.value).width;
    ctx.fillText(s.value, x + (cellW - vw) / 2, gridY + 40);
    ctx.fillStyle = "#737373";
    ctx.font = "600 22px Manrope";
    const lw = ctx.measureText(s.label).width;
    ctx.fillText(s.label, x + (cellW - lw) / 2, gridY + 148);
  });

  // Availability pill (kept clear of the footer)
  let footY = Math.min(gridY + 280, H - 260);
  const avail = AVAIL_LABELS[profile.availability];
  if (avail) {
    ctx.font = "600 28px Manrope";
    const tw = ctx.measureText(avail.text).width;
    ctx.strokeStyle = avail.color;
    ctx.lineWidth = 2;
    ctx.strokeRect(90, footY, tw + 90, 62);
    ctx.fillStyle = avail.color;
    ctx.beginPath();
    ctx.arc(122, footY + 31, 9, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillText(avail.text, 148, footY + 18);
  }

  // Footer
  ctx.fillStyle = "#737373";
  ctx.font = "600 28px Manrope";
  const fmText = "FIND ME ON";
  const fmWidth = ctx.measureText(fmText).width;
  ctx.fillText(fmText, 92, H - 140);
  ctx.fillStyle = "#ffffff";
  ctx.font = "44px Anton";
  ctx.fillText("EGNCY.NET", 92 + fmWidth + 28, H - 152);
}

export default function ShareCardDialog({ profile, career, onClose }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await drawCard(canvasRef.current, profile, career);
        if (!cancelled) setReady(true);
      } catch (e) {
        console.error("Share card render failed", e);
        toast.error("Could not render your share card");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toBlob = () =>
    new Promise((resolve) => canvasRef.current.toBlob(resolve, "image/png"));

  const download = async () => {
    const blob = await toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `egncy-${(profile.name || "player").toLowerCase().replace(/\s+/g, "-")}.png`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Card downloaded — post it to your story");
  };

  const share = async () => {
    const blob = await toBlob();
    const file = new File([blob], "egncy-card.png", { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "My EGNCY card" });
      } catch {
        // user cancelled share sheet
      }
    } else {
      await download();
    }
  };

  return (
    <div
      data-testid="share-card-dialog"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#121212] border border-[#262626] w-full max-w-md rounded-sm p-5 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl uppercase tracking-tight text-white">Share card</h2>
          <button data-testid="share-card-close" onClick={onClose} className="text-[#a3a3a3] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-[#a3a3a3]">
          Your stats as an Instagram-ready card. Download it and post it to pull teammates onto EGNCY.
        </p>
        <div className="border border-[#262626] rounded-sm overflow-hidden bg-[#050505]">
          <canvas
            ref={canvasRef}
            width={W}
            height={H}
            data-testid="share-card-canvas"
            className="w-full h-auto block"
          />
        </div>
        <div className="flex gap-2">
          <button
            data-testid="share-card-download"
            onClick={download}
            disabled={!ready}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-black font-semibold rounded-sm hover:bg-gray-200 disabled:opacity-60 transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            Download PNG
          </button>
          <button
            data-testid="share-card-share"
            onClick={share}
            disabled={!ready}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-[#404040] text-white hover:bg-[#1a1a1a] rounded-sm disabled:opacity-60 transition-colors text-sm"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
        </div>
      </div>
    </div>
  );
}
